import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Eye, 
  TrendingUp, 
  Calendar, 
  Clock,
  ArrowLeft,
  CreditCard,
  MapPin,
  Smartphone,
  Monitor,
  Globe,
  History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEVICE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];
const LOCATION_COLORS = ['#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e', '#f97316'];

const AnalyticsPage = () => {
  const { cardId } = useParams();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(cardId || '');
  const [analytics, setAnalytics] = useState(null);
  const [overview, setOverview] = useState(null);
  const [scanHistory, setScanHistory] = useState(null);

  useEffect(() => {
    const fetchCards = async () => {
      try {
        const [cardsRes, overviewRes] = await Promise.all([
          axios.get(`${API_URL}/api/cards`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/api/analytics/overview`, { headers: getAuthHeaders() })
        ]);
        setCards(cardsRes.data);
        setOverview(overviewRes.data);
        
        if (!selectedCard && cardsRes.data.length > 0) {
          setSelectedCard(cardsRes.data[0].id);
        }
      } catch (error) {
        toast.error('Failed to load analytics');
      }
    };
    fetchCards();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!selectedCard) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const [analyticsRes, historyRes] = await Promise.all([
          axios.get(`${API_URL}/api/cards/${selectedCard}/analytics`, { headers: getAuthHeaders() }),
          axios.get(`${API_URL}/api/cards/${selectedCard}/scan-history`, { headers: getAuthHeaders() })
        ]);
        setAnalytics(analyticsRes.data);
        setScanHistory(historyRes.data);
      } catch (error) {
        toast.error('Failed to load card analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [selectedCard]);

  // Process location data for display
  const getLocationBreakdown = () => {
    if (!scanHistory?.views || scanHistory.views.length === 0) return [];
    
    const locationCounts = {};
    scanHistory.views.forEach(view => {
      const location = view.location || 'Unknown';
      // Extract country or use full location
      const country = location.split(',').pop()?.trim() || location;
      locationCounts[country] = (locationCounts[country] || 0) + 1;
    });
    
    return Object.entries(locationCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  };

  // Process device data for display
  const getDeviceBreakdown = () => {
    if (!scanHistory?.views || scanHistory.views.length === 0) return [];
    
    const deviceCounts = {};
    scanHistory.views.forEach(view => {
      const device = view.device_type || 'Unknown';
      deviceCounts[device] = (deviceCounts[device] || 0) + 1;
    });
    
    return Object.entries(deviceCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  const locationData = getLocationBreakdown();
  const deviceData = getDeviceBreakdown();

  const selectedCardData = cards.find(c => c.id === selectedCard);

  if (loading && !overview) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="loading-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
              <p className="text-muted-foreground mt-1">Track your card performance</p>
            </div>
          </div>
          
          {cards.length > 0 && (
            <Select value={selectedCard} onValueChange={setSelectedCard}>
              <SelectTrigger className="w-[250px]" data-testid="card-selector">
                <SelectValue placeholder="Select a card" />
              </SelectTrigger>
              <SelectContent>
                {cards.map((card) => (
                  <SelectItem key={card.id} value={card.id}>
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      {card.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {cards.length === 0 ? (
          <Card className="empty-state border-dashed">
            <TrendingUp className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No analytics yet</h3>
            <p className="text-muted-foreground mb-6 max-w-sm">
              Create your first card to start tracking views and scans.
            </p>
            <Link to="/cards/new">
              <Button data-testid="create-card-btn">Create Your First Card</Button>
            </Link>
          </Card>
        ) : (
          <>
            {/* Overview Stats */}
            <div className="stats-grid">
              <Card className="border-border/50 hover:shadow-md transition-shadow duration-200">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Scans</CardTitle>
                  <Eye className="w-5 h-5 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" data-testid="total-scans">{analytics?.total_scans || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">All time</p>
                </CardContent>
              </Card>

              <Card className="border-border/50 hover:shadow-md transition-shadow duration-200">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Today</CardTitle>
                  <Clock className="w-5 h-5 text-emerald-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" data-testid="scans-today">{analytics?.scans_today || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">Scans today</p>
                </CardContent>
              </Card>

              <Card className="border-border/50 hover:shadow-md transition-shadow duration-200">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">This Week</CardTitle>
                  <TrendingUp className="w-5 h-5 text-amber-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" data-testid="scans-week">{analytics?.scans_this_week || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">Last 7 days</p>
                </CardContent>
              </Card>

              <Card className="border-border/50 hover:shadow-md transition-shadow duration-200">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">This Month</CardTitle>
                  <Calendar className="w-5 h-5 text-purple-500" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" data-testid="scans-month">{analytics?.scans_this_month || 0}</div>
                  <p className="text-xs text-muted-foreground mt-1">Last 30 days</p>
                </CardContent>
              </Card>
            </div>

            {/* Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Scan History</span>
                  {selectedCardData && (
                    <Badge variant="secondary" className="font-normal">
                      {selectedCardData.name}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <div className="loading-spinner" />
                  </div>
                ) : analytics?.scan_history && analytics.scan_history.length > 0 ? (
                  <div className="chart-container" data-testid="scan-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={analytics.scan_history}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorScans" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="date" 
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getMonth() + 1}/${date.getDate()}`;
                          }}
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          allowDecimals={false}
                        />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                          }}
                          labelFormatter={(value) => {
                            const date = new Date(value);
                            return date.toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            });
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorScans)"
                          name="Scans"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[300px] text-center">
                    <TrendingUp className="w-12 h-12 text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No scan data yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Share your card to start tracking views
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location & Device Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Location Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-rose-500" />
                    Scan Locations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center h-[200px]">
                      <div className="loading-spinner" />
                    </div>
                  ) : locationData.length > 0 ? (
                    <div className="space-y-3">
                      {locationData.map((item, index) => (
                        <div key={item.name} className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: LOCATION_COLORS[index % LOCATION_COLORS.length] }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium truncate">{item.name}</span>
                              <span className="text-sm text-muted-foreground ml-2">{item.value}</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full mt-1 overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all"
                                style={{ 
                                  width: `${(item.value / (scanHistory?.total_views || 1)) * 100}%`,
                                  backgroundColor: LOCATION_COLORS[index % LOCATION_COLORS.length]
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[200px] text-center">
                      <MapPin className="w-10 h-10 text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground text-sm">No location data yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Device Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-blue-500" />
                    Device Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center h-[200px]">
                      <div className="loading-spinner" />
                    </div>
                  ) : deviceData.length > 0 ? (
                    <div className="flex items-center gap-6">
                      <div className="w-32 h-32 flex-shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={deviceData}
                              cx="50%"
                              cy="50%"
                              innerRadius={25}
                              outerRadius={50}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              {deviceData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 space-y-2">
                        {deviceData.map((item, index) => (
                          <div key={item.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: DEVICE_COLORS[index % DEVICE_COLORS.length] }}
                              />
                              <span className="text-sm flex items-center gap-1">
                                {item.name === 'Mobile' && <Smartphone className="w-3 h-3" />}
                                {item.name === 'Desktop' && <Monitor className="w-3 h-3" />}
                                {item.name === 'Tablet' && <Monitor className="w-3 h-3" />}
                                {item.name}
                              </span>
                            </div>
                            <span className="text-sm font-medium">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[200px] text-center">
                      <Smartphone className="w-10 h-10 text-muted-foreground/50 mb-3" />
                      <p className="text-muted-foreground text-sm">No device data yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* View Full History Link */}
            {selectedCard && scanHistory?.total_views > 0 && (
              <Card className="bg-muted/30">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <History className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium">View Complete History</p>
                        <p className="text-sm text-muted-foreground">
                          See detailed scan history with timestamps and viewer information
                        </p>
                      </div>
                    </div>
                    <Link to={`/cards/${selectedCard}/history`}>
                      <Button variant="outline" data-testid="view-history-btn">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Cards Performance */}
            {overview?.cards && overview.cards.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>All Cards Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {overview.cards.map((card) => (
                      <div 
                        key={card.id}
                        className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                          card.id === selectedCard ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50 hover:bg-muted'
                        }`}
                        data-testid={`card-stat-${card.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            card.id === selectedCard ? 'bg-primary text-primary-foreground' : 'bg-background'
                          }`}>
                            <CreditCard className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-medium">{card.name}</p>
                            <p className="text-sm text-muted-foreground">{card.scan_count} total scans</p>
                          </div>
                        </div>
                        <Button
                          variant={card.id === selectedCard ? "default" : "ghost"}
                          size="sm"
                          onClick={() => setSelectedCard(card.id)}
                          data-testid={`view-analytics-${card.id}`}
                        >
                          {card.id === selectedCard ? 'Selected' : 'View'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
